import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Grid,
  CircularProgress,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Switch,
  Chip,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify/Iconify';
import { useGetMatchesQuery } from 'src/api/matches/getMatches';
import { visibleLanguages } from 'src/config/quill-text-editor-settings';
import { LanguageTabs } from 'src/components/language-tabs';
import { TabPanel } from 'src/components/language-tabs/TabPanel';
import { useUpdateRoundMutation } from 'src/api/contest-weeks/updateRound';
import {
  CreateRoundValidationSchema,
  useCreateRoundMutation,
} from 'src/api/contest-weeks/createRound';
import { WeekStatus } from 'src/enums/contest.enum';
import { MatchType, WeekType } from 'src/types/domain.types';
import { AnimatedList } from 'src/components/AnimatedList';
import { toast } from 'react-toastify';
import { LocalizedDateTimePicker } from 'src/components/form/LocalizedDateTimePicker';
import { dateUtils } from 'src/utils/date';
import { useTimezoneStore } from 'src/store/timezoneStore';

interface ContestRoundDialogProps {
  contestId: number;
  onClose: () => void;
  round: WeekType;
}

const defaultRound = {
  contest_week_id: 0,
  name_en: '',
  name_fr: '',
  name_es: '',
  name_lt: '',
  name_ru: '',
  name_de: '',
  name_sp: '',
  name_ca: '',
  status: WeekStatus.Draft,
  start_utc: '',
  end_utc: '',
  entry_fee: 0,
  commission_fee: 0,
  is_buy_in_enabled: false,
  is_back_in_enabled: false,
  matches: [],
};

export const ContestRoundDialog = ({
  contestId,
  onClose,
  ...props
}: ContestRoundDialogProps) => {
  const { timezone } = useTimezoneStore();

  const [matchToAdd, setMatchToAdd] = useState(null);
  const [round, setRound] = useState<WeekType>(defaultRound);
  const [value, setValue] = useState(0);

  const handleChangeTabs = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  const { data: matches = [] } = useGetMatchesQuery();
  const { mutateAsync: updateRound, isPending: isUpdatingRound } =
    useUpdateRoundMutation(contestId);
  const { mutateAsync: createRound, isPending: isCreatingRound } =
    useCreateRoundMutation(contestId);

  useEffect(() => {
    if (props.round) {
      setRound(props.round);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeStatus = (value: WeekStatus) => {
    setRound((prevState: any) => ({
      ...prevState,
      status: value,
    }));
  };

  const handleChange = (e: any) => {
    const { id, value } = e.target;

    setRound((prevState: any) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleDateChange = (newValue: any, fieldName: any) => {
    setRound((prevState: any) => ({
      ...prevState,
      [fieldName]: newValue,
    }));
  };

  const saveRoundHandler = () => {
    try {
      CreateRoundValidationSchema.parse(round);
    } catch (error) {
      console.log(error);
      return;
    }

    round.matches = round.matches.map((match: any) => match.match_id);

    if (round.contest_week_id) {
      updateRound(round);
    } else {
      createRound(round);
    }

    onClose();
  };

  const handleChangeToggle = (event: any, field: string) => {
    setRound((prevState: WeekType) => ({
      ...prevState,
      [field]: event.target.checked,
    }));
  };

  const handleChangeMatchToAdd = (event: any) => {
    setMatchToAdd(event.target.value);
  };

  const handleDeleteMatch = (matchId: number | string) => {
    round.matches = round.matches.filter(
      (match: MatchType) => match.match_id !== matchId
    );

    setRound({ ...round });
  };

  const matchAlreadyInRound = useMemo(
    () =>
      round.matches.find((match: MatchType) => match.match_id === matchToAdd),
    [round.matches, matchToAdd]
  );

  const handleAddMatch = () => {
    const match = matches.find((match) => match.match_id === matchToAdd);
    if (!match) return;

    if (matchAlreadyInRound) {
      toast.info('Match already in round');
      return;
    }

    round.matches.push(match);
    setRound({ ...round });
  };

  return (
    <Dialog
      open={true}
      onClose={() => {}}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth
      maxWidth='md'
    >
      <DialogTitle id='alert-dialog-title'>Round</DialogTitle>
      <DialogContent>
        {isCreatingRound || isUpdatingRound ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center' }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : !round ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center' }}
            style={{ padding: 20 }}
          >
            Sorry, no round found
          </Box>
        ) : (
          <Grid container spacing={2} mt={1}>
            <Grid item xs={4}>
              <LocalizedDateTimePicker
                label='Round Start Date'
                value={round.start_utc}
                onChange={(value: string) => {
                  handleDateChange(value, 'start_utc');
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <LocalizedDateTimePicker
                label='Round End Date'
                value={round.end_utc}
                onChange={(value: string) => handleDateChange(value, 'end_utc')}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id='status-select-label'>Status</InputLabel>
                <Select
                  labelId='status-label'
                  id='status-select'
                  value={round.status}
                  label='Type'
                  onChange={(e) =>
                    handleChangeStatus(e.target.value as WeekStatus)
                  }
                  variant='outlined'
                >
                  <MenuItem value={WeekStatus.Draft}>DRAFT</MenuItem>
                  <MenuItem value={WeekStatus.Active}>ACTIVE</MenuItem>
                  <MenuItem value={WeekStatus.Upcoming}>UPCOMING</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ m: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={round.is_back_in_enabled}
                      onChange={(event) =>
                        handleChangeToggle(event, 'is_back_in_enabled')
                      }
                    />
                  }
                  label='Back IN'
                />
              </FormGroup>
            </Grid>
            <Grid item sx={{ m: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={round.is_buy_in_enabled}
                      onChange={(event) =>
                        handleChangeToggle(event, 'is_buy_in_enabled')
                      }
                    />
                  }
                  label='Buy IN'
                />
              </FormGroup>
            </Grid>

            <Grid item>
              <LanguageTabs value={value} onChange={handleChangeTabs} />
              {visibleLanguages.map((lang, index) => (
                <TabPanel key={lang.code} value={value} index={index}>
                  <TextField
                    id={`name_${lang.code}`}
                    label={`Name ${lang.label}`}
                    type='text'
                    fullWidth
                    value={round[`name_${lang.code}` as keyof WeekType]}
                    variant='standard'
                    onChange={handleChange}
                  />

                  <TextField
                    id={`description_${lang.code}`}
                    label={`Description ${lang.label}`}
                    type='text'
                    fullWidth
                    value={round[`description_${lang.code}` as keyof WeekType]}
                    variant='standard'
                    onChange={handleChange}
                    multiline
                    minRows={1}
                  />
                </TabPanel>
              ))}
            </Grid>

            <Grid item xs={6}>
              <TextField
                id='entry_fee'
                label='Entry Fee'
                type='number'
                fullWidth
                value={round.entry_fee}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id='commission_fee'
                label='Commission Fee'
                type='number'
                fullWidth
                value={round.commission_fee}
                onChange={handleChange}
              />
            </Grid>

            <Typography variant='h6' sx={{ mx: 2, mt: 2 }}>
              Matches
            </Typography>
            <Grid item xs={12} sm={12} md={12}>
              <AnimatedList
                list={round.matches}
                onDelete={handleDeleteMatch}
                getItemId={(match) => match.match_id}
                gridColumns='2fr 60px'
                renderItem={(match) => (
                  <Grid
                    key={match.match_id}
                    container
                    sx={{ m: 1, width: '100%' }}
                  >
                    <Grid item xs={11}>
                      <Typography sx={{ m: 1, width: '100%' }} variant='body1'>
                        {match.name_en ?? `Match ${match.match_id}`}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              />
              <FormControl sx={{ m: 1, minWidth: 320 }} size='small'>
                <InputLabel id='demo-simple-select-label'>Match</InputLabel>
                <Select
                  labelId='demo-simple-select-label'
                  id='demo-simple-select'
                  value={matchToAdd}
                  label='Match'
                  onChange={handleChangeMatchToAdd}
                >
                  {matches.map((match) => (
                    <MenuItem
                      value={match.match_id}
                      key={match.match_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        {match.name_en ?? `Match ${match.match_id}`}
                      </Typography>
                      <Chip
                        label={dateUtils.formatForDisplay(
                          match.start_utc,
                          timezone
                        )}
                        size='small'
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant='contained'
                startIcon={<Iconify icon='eva:save-outline' />}
                onClick={() => handleAddMatch()}
                sx={{ m: 1 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => saveRoundHandler()} autoFocus disabled={false}>
          {round?.contest_week_id ? 'Edit' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
